/**
 * `useWorkspacePinActions` — the single create / update / unpin orchestration
 * for the workspace pin feature (Task 18). Extracted from `WorkspacePinButton`
 * so the tab-strip context menu (Task 22) can drive the exact same mutations
 * and fire byte-identical analytics without a second implementation.
 *
 * Each action: capture the tab's live definition → build the ref/patch via the
 * pure builders in `workspacePinCreate.ts` → mutate `pinnedObjectsStore` → on
 * success, update the tab's `sourcePin` link + `justPinnedStore` flash signal
 * (create only) → fire the `feature:workspace_pin*` event. Failures degrade
 * silently (no capture registered, no connection/database, store rejection).
 *
 * `createPin` is single-flight per tabId and re-validates the tab's
 * connection/database identity after `pin()` resolves before linking (F2,
 * Codex gate-3) — see `inFlightCreates` below. Analytics props for
 * update/unpin are `workspacePinTelemetry(pin)` (`{workspaceType, source}`),
 * matching `pinnedWorkspaceContextMenuService.ts`'s tree-row actions (F4).
 */

import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { usePinnedObjectsStore } from '../../../database-objects/store/pinnedObjectsStore';
import { useJustPinnedStore } from '../../../database-objects/store/justPinnedStore';
import { capturePinDefinition } from '../services/pinCaptureRegistry';
import {
  buildPinnedWorkspaceRef,
  buildWorkspacePinPatch,
  isFileSourcePin,
  workspacePinTelemetry,
} from '../services/workspacePinCreate';
import { pinScopeForTab } from '../services/comparePin';
import type { Tab } from '../../core/types/workspaceTypes';
import type { PinnedWorkspaceRef } from '../../../../types/electron';

/** File path backing a query/notebook tab (used for the G4 file-source rule). */
function filePathOf(tab: Tab | undefined): string | undefined {
  if (!tab) return undefined;
  if (tab.type === 'query' || tab.type === 'notebook') return tab.content.filePath;
  return undefined;
}

/**
 * Tabs with a `createPin` currently in flight (F2, Codex gate-3). Module-level
 * — not per-hook-instance — because `useWorkspacePinActions(tabId)` is
 * instantiated independently by both `WorkspacePinButton` and the tab-strip
 * context menu for the SAME tab; a per-instance guard wouldn't stop two rapid
 * clicks routed through different call sites. Mirrors the `inFlightOpens`
 * idiom in `openWorkspacePin.ts`. Entries self-clear in a `.finally()`.
 */
const inFlightCreates = new Set<string>();

export interface WorkspacePinActions {
  /** Create a fresh pin from the tab's live definition and link the tab to it. */
  createPin: () => void;
  /** Re-snapshot the linked pin from the current live definition (diverged Update). */
  updatePin: (pin: PinnedWorkspaceRef) => void;
  /** Remove the linked pin and sever the tab's link. */
  unpin: (pin: PinnedWorkspaceRef) => void;
}

export function useWorkspacePinActions(tabId: string): WorkspacePinActions {
  const { track } = useAnalytics();

  const createPin = () => {
    // F2 — a create already in flight for this tab wins; a second rapid call
    // (double-click, two menu instances) is a no-op rather than minting a
    // second, duplicate pin before the first links.
    if (inFlightCreates.has(tabId)) return;
    const captured = capturePinDefinition(tabId);
    if (!captured) return; // degrade gracefully — no capture registered yet
    const ws = useWorkspaceStore.getState();
    const tab = ws.getTab(tabId);
    // B5 — NOT `tab.connectionInfo`: a comparison is filed under its SOURCE
    // connection, which compare tabs carry in `content.source` and populate in
    // `connectionInfo` inconsistently (target-derived, or absent).
    const { connectionId, database } = pinScopeForTab(tab);
    if (!connectionId || !database) return;
    const capturedFilePath = filePathOf(tab);
    const ref = buildPinnedWorkspaceRef({
      captured,
      database,
      tabTitle: tab?.title ?? 'Untitled',
      filePath: capturedFilePath,
    });
    inFlightCreates.add(tabId);
    void usePinnedObjectsStore
      .getState()
      .pin(connectionId, ref)
      .then(() => {
        // F2 — the tab may have rebound to a different connection/database
        // (or closed) while `pin()` was in flight. Linking the connection
        // captured at create-time to the now-different tab would attach the
        // wrong pin, so skip the link on any mismatch — the pin itself was
        // still created, just left unlinked; nothing to celebrate either, so
        // the flash + analytics are skipped too.
        // C1 (Codex final-gate) — a FILE pin's tab may have been Saved As to a
        // DIFFERENT path while `pin()` was in flight, BEFORE `sourcePin` was
        // assigned (so the store's Save-As sever couldn't fire). Linking then
        // would tie the new-file tab to a pin built from the OLD path, risking
        // a wrong-file overwrite on a later Save — so a file-identity CHANGE
        // disqualifies the link.
        // BUT (G4, spec §"Snapshot link × first Save") a SNAPSHOT pin
        // (`capturedFilePath === undefined`) whose tab receives its FIRST Save
        // mid-flight MUST still link — a snapshot link is designed to survive
        // the tab becoming file-backed. So only a pin that was ALREADY
        // file-backed and whose path then changed is disqualified; the
        // `undefined → "/x.sql"` first-Save case links normally.
        const latestTab = useWorkspaceStore.getState().getTab(tabId);
        const fileIdentityChanged =
          capturedFilePath !== undefined && filePathOf(latestTab) !== capturedFilePath;
        // Re-validated through the SAME seam the scope came from, so a compare
        // tab whose source endpoint was re-pointed mid-flight is caught too.
        const latestScope = pinScopeForTab(latestTab);
        const stillMatches =
          latestTab !== undefined &&
          latestScope.connectionId === connectionId &&
          latestScope.database === database &&
          !fileIdentityChanged;
        if (!stillMatches) return;
        useWorkspaceStore.getState().setSourcePin(tabId, { connectionId, pinId: ref.id });
        useJustPinnedStore.getState().set(ref.id);
        track('feature:workspace_pin', workspacePinTelemetry(ref));
      })
      .catch(() => {})
      .finally(() => {
        inFlightCreates.delete(tabId);
      });
  };

  const updatePin = (pin: PinnedWorkspaceRef) => {
    const ws = useWorkspaceStore.getState();
    const tab = ws.getTab(tabId);
    const scope = pinScopeForTab(tab);
    const connectionId = tab?.sourcePin?.connectionId ?? scope.connectionId;
    const database = scope.database;
    if (!connectionId || !database) return;
    // B5 — mirrors the same guard in `pinnedWorkspaceContextMenuService`'s
    // `divergedLinkedTabs`: a comparison re-pointed at a DIFFERENT source
    // connection can't be updated in place, because the pin lives in the old
    // connection's file. Unpin and re-pin is the honest path. (Other kinds get
    // their `sourcePin` severed by the store on a connection change, so this
    // never fires for them.)
    if (scope.connectionId !== undefined && scope.connectionId !== connectionId) return;

    // G1 (bug fix — Task 25 e2e caught this) — a file-source pin's content is
    // inert; per `pinDivergence.ts`'s F2 rule, the ONLY way a file pin can be
    // diverged at all is a database rebind. Falling through to the generic
    // snapshot patch below would call `capturePinDefinition` (which for
    // query/notebook tabs ALWAYS returns a text/cells snapshot, file-backed or
    // not — see `QueryEditorTab`/`NotebookTab`'s capture comments) and write
    // that snapshot over the pin's payload, silently converting a file pin to
    // a text/cells pin the moment its tab's database changes. Patch `database`
    // alone instead — mirrors `updatePinFromLinkedTab` in
    // `pinnedWorkspaceContextMenuService.ts` (the tree row's "Update from open
    // tab" action), which already guards this correctly; the two Update
    // surfaces must agree.
    if (isFileSourcePin(pin)) {
      void usePinnedObjectsStore
        .getState()
        .updatePin(connectionId, pin.id, { database })
        .then(() => track('feature:workspace_pin_update', workspacePinTelemetry(pin)))
        .catch(() => {});
      return;
    }

    const captured = capturePinDefinition(tabId);
    if (!captured) return;
    const patch = buildWorkspacePinPatch({ captured, database });
    void usePinnedObjectsStore
      .getState()
      .updatePin(connectionId, pin.id, patch)
      // F4 — match the tree's `workspacePinAnalyticsFields(ref)`: full
      // `{workspaceType, source}`, not just `workspaceType`.
      .then(() => track('feature:workspace_pin_update', workspacePinTelemetry(pin)))
      .catch(() => {});
  };

  const unpin = (pin: PinnedWorkspaceRef) => {
    const ws = useWorkspaceStore.getState();
    const tab = ws.getTab(tabId);
    const connectionId = tab?.sourcePin?.connectionId ?? pinScopeForTab(tab).connectionId;
    if (!connectionId) return;
    void usePinnedObjectsStore
      .getState()
      .unpin(connectionId, pin)
      .then(() => {
        useWorkspaceStore.getState().setSourcePin(tabId, undefined);
        // F4 — rename to match the tree's `feature:workspace_pin_unpin` (was
        // `feature:workspace_unpin`) and carry the full `{workspaceType, source}`.
        track('feature:workspace_pin_unpin', workspacePinTelemetry(pin));
      })
      .catch(() => {});
  };

  return { createPin, updatePin, unpin };
}
